import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import Button from '../Button/Button';
import InputField from '../InputField/InputField';
import SelectOptions from '../SelectOptions/SelectOptions';
import SnackBar from '../SnackBar/SnackBar';

import InformationIcon from '../../assets/svgs/InformationIcon';
import UploadIcon from '../../assets/svgs/UploadIcon';

import Api from '../../libs/Api';
import translate from '../../libs/i18n';
import sleep from '../../utils/sleep';
import { useIsMounted } from '../../utils/useIsMounted';

import { SendHelpEmailFormStyle } from './SendHelpEmailFormStyle';

const SendHelpEmailForm = ({ loading, setLoading, setActionComplete }) => {
  const inputFileRef = useRef();
  const isMounted = useIsMounted();
  const language = useSelector((state) => state.settings.language);

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [imageFiles, setImageFiles] = useState([]);
  const [institution, setInstitution] = useState('');
  const [institutionOptions, setInstitutionOptions] = useState([]);
  const [loadDataWarning, setLoadDataWarning] = useState();
  const [message, setMessage] = useState('');
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [showUploadInstructions, setShowUploadInstructions] = useState('');
  const [tooManyImageError, setTooManyImageError] = useState('');
  const [topic, setTopic] = useState('');
  const [waitInstitution, setWaitInstitution] = useState(true);

  useEffect(() => {
    (async () => {
      if (!isMounted() || !waitInstitution) {
        return;
      }

      setLoading(true);
      setWaitInstitution(false);

      const response = await Api.getInstitution();

      if (
        waitInstitution &&
        response &&
        response.status &&
        response.status === 200
      ) {
        const currentInstitutionOptions = [];

        if (
          response.data &&
          response.data.result &&
          response.data.result.length
        ) {
          response.data.result.forEach((institutionItem) => {
            if (
              institutionItem &&
              institutionItem.id &&
              institutionItem.name &&
              institutionItem.name.length
            ) {
              currentInstitutionOptions.push({
                key: institutionItem.id,
                value: institutionItem.id,
                text: institutionItem.name,
              });
            }
          });

          setInstitutionOptions(currentInstitutionOptions);
        }
        setLoading(false);
      } else {
        setLoading(false);
        setLoadDataWarning({
          type: 'error',
          text: 'errorTryingToLoadInitialDataTryingLoadAgain',
        });

        await sleep(1000 * 5);

        setWaitInstitution(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMounted, language, waitInstitution]);

  useEffect(() => {
    if (!loading) {
      const timeout = setTimeout(() => {
        setLoadDataWarning(null);
      }, 3500);

      return () => clearTimeout(timeout);
    }

    return null;
  }, [loading]);

  const topicOptionList = useMemo(() => {
    return [
      { key: 0, value: '', text: translate('selectATopic', language) },
      {
        key: 1,
        value: 'General Suport',
        text: translate('generalSuport', language),
      },
      {
        key: 2,
        value: 'Healthscan Plan',
        text: translate('healthscanPlan', language),
      },
      {
        key: 3,
        value: 'Change admin Institution',
        text: translate('changeAdminInstitution', language),
      },
    ];
  }, [language]);

  const handleChangeSelect = (value, options, setValue) => {
    const currentOption = options.find(
      (option) => `${option.value}` === `${value}`
    );

    if (currentOption) {
      setValue(currentOption.value);
    }
  };

  const onChangeInputFile = (event) => {
    if (
      event &&
      event.target &&
      event.target.files &&
      event.target.files.length
    ) {
      const currentImageFiles = [];

      for (let i = 0; i < event.target.files.length; i += 1) {
        const image = event.target.files[i];

        if (!imageFiles.find((imageFile) => imageFile.name === image.name)) {
          currentImageFiles.push(image);
        }
      }

      if (currentImageFiles.length) {
        setImageFiles([...currentImageFiles]);
      }
    }

    inputFileRef.current.files = null;
  };

  const handleSendMessage = () => {
    let hasError = false;

    if (!name) {
      setNameError('theNameFieldiSRequired');
      hasError = true;
    }

    if (!email) {
      setEmailError('emailFieldIsRequired');
      hasError = true;
    }

    if (imageFiles.length > 4) {
      hasError = true;

      setTooManyImageError('itsNotPossibleSendMoreThan4ImagesByEmail');
    }

    if (!hasError) {
      const submitData = new FormData();

      submitData.append('topic', topic);
      submitData.append('name', name);
      submitData.append('email', email);
      submitData.append('institution', institution);
      submitData.append('message', message);

      imageFiles.forEach((image, index) => {
        submitData.append(
          `file${index + 1}`,
          image,
          `Screenshot from ${new Date().getFullYear()}-${new Date().getMonth()}-${new Date().getDate()} ${new Date().getHours()}-${new Date().getMinutes()}-${new Date().getSeconds()}`
        );
      });

      const apiData = {
        method: 'POST',
        body: submitData,
        redirect: 'follow',
      };

      setLoading(true);

      Api.postSendEmail(apiData.body)
        // .then((res) => console.log(res))
        .then((res) => res)
        // .catch((error) => console.log(error))
        .catch((error) => error)
        .finally(() => {
          setActionComplete({
            type: 'success',
            text: 'Email enviado com sucesso.',
          });
          setLoading(false);
        });

      return true;
    }

    return null;
  };

  const onShowUploadInstructions = () => {
    if (showUploadInstructions) {
      setShowUploadInstructions('');
    } else {
      setShowUploadInstructions(' opened');
    }
  };

  return (
    <SendHelpEmailFormStyle>
      <div className="support-form-text">
        <span className="support-text-title">
          {translate('howCanWeHelpYou', language)}
        </span>
        <p className="support-text">
          {`${translate(
            'pleaseFillOutThisFormToGetInTouch',
            language
          )}. ${translate('ourOfficeHoursAre', language)}.`}
        </p>
      </div>
      <div className="input-container">
        <span className="label-input">{translate('topic', language)}</span>
        <div className="select-container">
          <SelectOptions
            className="input-form"
            theme="soft-blue-outline"
            name="topic"
            id="topic"
            options={topicOptionList}
            value={topic}
            onChange={(value) =>
              handleChangeSelect(value, topicOptionList, setTopic)
            }
          />
        </div>
      </div>
      <div className="input-container">
        <span className="label-input">{translate('name', language)}</span>
        <InputField
          className="input-form"
          type="text"
          inputName="name"
          value={name}
          noMarginBottom
          onChange={setName}
          placeholder={translate('enterYourName', language)}
          error={
            nameError && nameError.length
              ? `${translate(nameError, language)}.`
              : ''
          }
        />
      </div>
      <div className="input-container">
        <span className="label-input">Email</span>
        <InputField
          className="input-form"
          type="text"
          inputName="email"
          value={email}
          noMarginBottom
          onChange={setEmail}
          placeholder={translate('exampleEmail', language)}
          error={
            emailError && emailError.length
              ? `${translate(emailError, language)}.`
              : ''
          }
        />
      </div>
      <div className="input-container">
        <span className="label-input">
          {translate('Institution', language)}
        </span>
        <div className="select-container">
          <SelectOptions
            className="input-form"
            theme="soft-blue-outline"
            name="institution"
            id="institution"
            options={[
              {
                key: 0,
                value: '',
                text: translate('selectInstitution', language),
              },
              ...institutionOptions,
            ]}
            value={institution}
            onChange={(value) =>
              handleChangeSelect(value, institutionOptions, setInstitution)
            }
          />
        </div>
      </div>
      <div className="input-container">
        <span className="label-input">{translate('message', language)}</span>
        <InputField
          className="input-form"
          type="text"
          inputName="message"
          value={message}
          noMarginBottom
          onChange={setMessage}
          placeholder={translate('enterAMessage', language)}
        />
      </div>
      <div className="input-container attachment-container">
        <span className="label-input attachment-icon">
          {translate('attachment', language)}
          <span
            onMouseOver={onShowUploadInstructions}
            onMouseOut={onShowUploadInstructions}
            onFocus
            onBlur
          >
            <InformationIcon />
          </span>
        </span>
        <div
          className={`input-container-content${
            tooManyImageError && tooManyImageError.length ? ' with-error' : ''
          }`}
        >
          <div className="add-image-container">
            {showUploadInstructions ? (
              <div className={`instruction${showUploadInstructions}`}>
                <span className="first-message">
                  {translate(
                    'attachPrintToHelpUnderstandWhatsHappening',
                    language
                  )}
                </span>
                <span className="second-message">
                  {translate('from1To4ImageFiles', language)}
                </span>
              </div>
            ) : null}
            <form
              className="add-image"
              onSubmit={(event) => event.preventDefault()}
            >
              <span className="icon">
                <UploadIcon />
              </span>
              <p className="form-instruction">
                {translate('uploadFiles', language).toUpperCase()}
              </p>
              <p className="input-file-extension">.png or .jpeg</p>
              <input
                ref={inputFileRef}
                type="file"
                className="input-file"
                accept="*/png,.png, image/png, */png, .png, */jpeg,.jpeg, image/jpeg, */jpeg, .jpeg"
                multiple
                onChange={onChangeInputFile}
              />
            </form>
          </div>
          {tooManyImageError && tooManyImageError.length ? (
            <span className="error-message">
              {translate(tooManyImageError, language)}
            </span>
          ) : null}
        </div>
      </div>
      <div className="submit-button-container">
        <Button
          type="button"
          theme="default"
          onClick={handleSendMessage}
          text={translate('submit', language)}
        />
      </div>
      {loadDataWarning &&
      loadDataWarning.type &&
      loadDataWarning.type.length &&
      loadDataWarning.text &&
      loadDataWarning.text.length ? (
        <div className="snack-bar-wrapper">
          <SnackBar
            type={loadDataWarning.type}
            text={translate(loadDataWarning.text, language)}
          />
        </div>
      ) : null}
    </SendHelpEmailFormStyle>
  );
};

export default SendHelpEmailForm;

SendHelpEmailForm.propTypes = {
  setLoading: PropTypes.func.isRequired,
  setActionComplete: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
};
